import type { Cra } from '@bloomays-lib/types.shared';

/** Répartition des jours d'un CRA — sous-ensemble de `Cra` suffisant au calcul des montants. */
export type FreelanceWorkedDays = Pick<
  Cra,
  'normalWorkedDays' | 'saturdaysWorkedDays' | 'sundayAndholidaysWorkedDays' | 'astreinte'
>;

/** Tarif de vente minimal — compatible `Pricing` (talents) et `IPricing` (bloomers). */
export type FreelanceSalesPricingInput = {
  salePricePerDayWithoutTaxes: number;
  astreinteCoef?: number;
};

/** Tarif d'achat minimal — compatible `Pricing` (talents) et `IPricing` (bloomers). */
export type FreelancePurchasePricingInput = {
  purchasePricePerDayWithoutTaxes: number;
  astreinteCoef?: number;
};

/** Entrée tarif minimale — compatible `Pricing` (talents) et `IPricing` (bloomers). */
export type FreelanceMarginPricingInput = FreelanceSalesPricingInput & FreelancePurchasePricingInput;

/** Montant HT décomposé jours normaux / HNO (heures non ouvrées) + total. */
export type FreelanceAmountParts = {
  /** Part jours ouvrés (jours travaillés normaux ×1). */
  normal: number;
  /** Part HNO : samedi ×1.5 + dimanche/férié ×2 + astreinte ×`astreinteCoef`. */
  hno: number;
  /** Somme normal + HNO. */
  total: number;
};

/**
 * Méthode de calcul des montants CRA, décomposée jours normaux / HNO : jours ouvrés ×1,
 * samedi ×1.5, dimanche / férié ×2, astreinte ×`astreinteCoef` (défaut 2). Cœur partagé
 * par le calcul de marge, de CA vente et de coût achat (mêmes coefficients que
 * `calculateActivityAmount` côté API).
 */
export const computeFreelanceAmountParts = (
  days: FreelanceWorkedDays,
  amountPerDay: number,
  astreinteCoef = 2,
): FreelanceAmountParts => {
  const normal = amountPerDay * days.normalWorkedDays;
  const hno =
    amountPerDay * 1.5 * days.saturdaysWorkedDays +
    amountPerDay * 2 * days.sundayAndholidaysWorkedDays +
    amountPerDay * astreinteCoef * days.astreinte;
  return { normal, hno, total: normal + hno };
};

/**
 * Marge mensuelle freelance à partir d’un CRA domaine et d’un tarif jour (glossaire / TEC-2767).
 */
export const computeFreelanceMargin = (cra: Cra, pricing: FreelanceMarginPricingInput): number => {
  const marginPerDay = pricing.salePricePerDayWithoutTaxes - pricing.purchasePricePerDayWithoutTaxes;
  return computeFreelanceAmountParts(cra, marginPerDay, pricing.astreinteCoef ?? 2).total;
};

/**
 * CA vente HT freelance à partir des jours d’un CRA et du prix de vente jour, en
 * réutilisant la même méthode de coefficients HNO. Sert au récap de sélection CRA.
 */
export const computeFreelanceSalesAmount = (days: FreelanceWorkedDays, pricing: FreelanceSalesPricingInput): number =>
  computeFreelanceAmountParts(days, pricing.salePricePerDayWithoutTaxes, pricing.astreinteCoef ?? 2).total;

/**
 * Coût achat HT freelance (montant reversé au Bloomer) à partir des jours d’un CRA
 * et du prix d’achat jour, avec la même méthode de coefficients HNO.
 */
export const computeFreelancePurchaseAmount = (
  days: FreelanceWorkedDays,
  pricing: FreelancePurchasePricingInput,
): number =>
  computeFreelanceAmountParts(days, pricing.purchasePricePerDayWithoutTaxes, pricing.astreinteCoef ?? 2).total;
